/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { storyblokEditable } from '@storyblok/react/rsc'
import { useTranslations } from 'next-intl'
import React, { DetailedHTMLProps, InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { MdCheckCircleOutline, MdOutlineApproval } from 'react-icons/md'

import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Container } from '@/components/ui/Container'
import { LoadingIndicator } from '@/components/ui/LoadingIndicator'
import { Typography } from '@/components/ui/Typography'
import { usePersonalisedSuggestionForm } from '@/hooks/forms/contact/usePersonalisedSuggestionForm'
import { PersonalisedSuggestionFormStoryblok } from '@/lib/storyblok/types/generated-types'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type PersonalisedSuggestionFormProps = {
  /** The Storyblok blok that represents this component. */
  blok: PersonalisedSuggestionFormStoryblok
}

/**
 * A Storyblok component for displaying the personalised suggestion form.
 */
const PersonalisedSuggestionForm: React.FC<PersonalisedSuggestionFormProps> = ({ blok }) => {
  const t = useTranslations('PersonalisedSuggestionForm')
  const { user_type, activity_scope, learning_objective, delivery_method, duration, target_audience } = blok
  const { formState, requestSuccess, requestPending, requestError, register, handleSubmit } =
    usePersonalisedSuggestionForm()

  return (
    <Container maxWidth={'6xl'} mxAuto gap={'lg'} className='relative' {...storyblokEditable(blok)}>
      <Typography variant={'h1'} as={'h1'} colour={'primary'} weight={'bold'} align={'center'} className={'pb-6'}>
        {t('mainHeading')}
      </Typography>

      {requestError && <Alert type={'error'} message={requestError} duration={5000} />}
      <div
        className={cn(
          'absolute! inset-0 flex flex-col gap-6 items-center justify-center text-center',
          'transition-opacity duration-1000',
          requestSuccess ? 'opacity-100 pointer-events-auto z-10' : 'opacity-0 pointer-events-none -z-10',
        )}
        aria-hidden={!requestSuccess}
      >
        <MdCheckCircleOutline
          className={'h-14 w-14 bg-white text-success-500 p-3 border border-grey-50/20 rounded-full'}
          aria-hidden='true'
        />
        <h1 className={'text-4xl font-medium text-primary-500'}>{t('thanksForRequestSuccessMessage')}</h1>
        <p className={'text-2xl text-primary-500'}>{t('teamReviewDetailsMessage')}</p>
      </div>
      <form
        onSubmit={handleSubmit}
        className={cn(
          'flex flex-col gap-5 justify-center items-center text-lg font-thin text-center',
          'transition-opacity duration-1000',
          requestSuccess ? 'opacity-0 pointer-events-none' : 'opacity-100 pointer-events-auto',
        )}
        aria-hidden={requestSuccess}
      >
        <div className={'flex flex-wrap items-center justify-center'}>
          <LabeledSelect
            id={'user-type'}
            label={t('iAm-selectLabel')}
            labelClassName={'mr-2'}
            options={user_type?.value ?? []}
            {...register('userType')}
            errorText={formState.errors.userType?.message}
            dirty={formState.dirtyFields.userType}
          />
          <LabeledSelect
            id={'activity-scope'}
            label={t('andWouldLikeA-selectLabel')}
            labelClassName={'mx-2'}
            options={activity_scope?.value ?? []}
            {...register('activityScope')}
            errorText={formState.errors.activityScope?.message}
            dirty={formState.dirtyFields.activityScope}
          />
          <LabeledSelect
            id={'learning-objective'}
            label={t('to-selectLabel')}
            labelClassName={'mx-2'}
            options={learning_objective?.value ?? []}
            {...register('learningObjective')}
            errorText={formState.errors.learningObjective?.message}
            dirty={formState.dirtyFields.learningObjective}
          />
          <span>.</span>
        </div>
        <div className={'flex flex-wrap items-center justify-center'}>
          <LabeledSelect
            id={'delivery-method'}
            label={t('theActivityShouldBe-selectLabel')}
            labelClassName={'mr-2'}
            options={delivery_method?.value ?? []}
            {...register('deliveryMethod')}
            errorText={formState.errors.deliveryMethod?.message}
            dirty={formState.dirtyFields.deliveryMethod}
          />
          <span>.</span>
          <LabeledSelect
            id={'duration'}
            label={t('itShouldLast-selectLabel')}
            labelClassName={'mx-2'}
            options={duration?.value ?? []}
            {...register('duration')}
            errorText={formState.errors.duration?.message}
            dirty={formState.dirtyFields.duration}
          />
          <span>.</span>
          <LabeledSelect
            id={'target-audience'}
            label={t('myAudienceAre-selectLabel')}
            labelClassName={'mx-2'}
            options={target_audience?.value ?? []}
            {...register('targetAudience')}
            errorText={formState.errors.targetAudience?.message}
            dirty={formState.dirtyFields.targetAudience}
          />
          <span>.</span>
        </div>
        <div>
          <span>{t('humanResponsesInfo')}</span>
        </div>
        <div className={'w-full max-w-full md:max-w-[80%]'}>
          <TextInput
            type={'textarea'}
            placeholder={t('additionalInfoInputPlaceholder')}
            {...register('additionalInfo')}
            errorText={formState.errors.additionalInfo?.message}
            dirty={formState.dirtyFields.additionalInfo}
            id={'product-finder-additional-info'}
          />
        </div>
        <div className={'flex flex-col w-full max-w-full gap-2 md:max-w-[80%] md:flex-row'}>
          <TextInput
            type={'text'}
            placeholder={t('nameInputPlaceholder')}
            {...register('name')}
            errorText={formState.errors.name?.message}
            dirty={formState.dirtyFields.name}
            required
            id={'product-finder-name'}
          />
          <TextInput
            type={'email'}
            placeholder={t('workEmailInputPlaceholder')}
            {...register('email')}
            errorText={formState.errors.email?.message}
            dirty={formState.dirtyFields.email}
            required
            id={'product-finder-email'}
          />
        </div>
        <Button
          type={'submit'}
          variant={'secondary'}
          rounded={'full'}
          size={'lg'}
          disabled={requestPending || requestSuccess}
          id={'product-finder-submit'}
        >
          {t('getRecommendationButtonText')}
          {requestPending && <LoadingIndicator size={'ml-3 w-6 h-6'} />}
          {requestSuccess && <MdOutlineApproval className={'ml-3 text-white'} aria-hidden='true' />}
        </Button>
      </form>
    </Container>
  )
}

/** A description of the available properties for this component. */
type LabeledSelectProps = Omit<DetailedHTMLProps<SelectHTMLAttributes<HTMLSelectElement>, HTMLSelectElement>, 'id'> & {
  /** The ID of the select element. */
  id: string

  /** The label for the select element. */
  label: string

  /** The options for the select element. */
  options: Array<string>

  /** Optional class name for the label. */
  labelClassName?: string

  /** The error message to display for the input. */
  errorText?: string

  /** Whether the input has been touched by the user. */
  dirty?: boolean
}

/**
 * A basic select element for use in the personalised suggestion form.
 */
const LabeledSelect: React.FC<LabeledSelectProps> = ({
  id,
  label,
  labelClassName,
  options,
  errorText,
  dirty,
  ...rest
}) => {
  return (
    <div className={'flex flex-col items-center text-center'}>
      <div className={'flex items-center text-center'}>
        <label htmlFor={id} className={labelClassName}>
          {label}
        </label>
        <select id={id} className={'bg-zinc-200 text-primary-500 text-lg py-1.5 px-2.5 rounded-full'} {...rest}>
          {options.map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      {errorText && dirty && <p className={'text-red-500 text-sm mt-1'}>{errorText}</p>}
    </div>
  )
}

/** A description of the available properties for this component. */
type TextInputProps = Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'type' | 'onChange'
> &
  Omit<DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>, 'onChange'> & {
    /** The type of the input. */
    type: 'textarea' | 'text' | 'email'

    /** The function to call when the input value changes. */
    onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void

    /** The error message to display for the input. */
    errorText?: string

    /** Whether the input has been touched by the user. */
    dirty?: boolean
  }

/**
 * A basic text input element for use in the personalised suggestion form.
 */
const TextInput: React.FC<TextInputProps> = ({ id, placeholder, type, errorText, dirty, ...rest }) => {
  const Component = type === 'textarea' ? 'textarea' : 'input'

  return (
    <div className={'flex flex-col w-full'}>
      <Component
        id={id}
        className={cn(
          'bg-zinc-200 text-primary-500 text-lg w-full py-1.5 px-5 shadow-inner',
          type === 'textarea' && 'rounded-3xl min-h-[100px]',
          type !== 'textarea' && 'rounded-full',
          errorText && dirty && 'border border-red-500',
        )}
        placeholder={placeholder}
        type={type === 'textarea' ? undefined : type}
        {...rest}
      />
      {errorText && dirty && <p className={'text-red-500 text-sm mt-1 text-left'}>{errorText}</p>}
    </div>
  )
}

export { PersonalisedSuggestionForm }
